import { useEffect } from "react";

/** Same gate as the `@media` block in styles.css — a mouse session never
    raises a keyboard, so this hook does nothing on a fine pointer or above
    the console's own mobile breakpoint, and detaches itself if the query
    stops matching (e.g. a phone rotated into a width that crosses it). */
const MOBILE_QUERY = "(pointer: coarse) and (max-width: 960px)";

/** See apps/client's useAppViewport for the timings below and the full trace
    of what a keyboard open actually moves on WebKit — this is the same fix,
    just switched on and off by MOBILE_QUERY instead of running everywhere. */
const KEYBOARD_SETTLE_MS = 600;
const STABILITY_MS = 80;

/** Mirrors apps/client's useAppViewport (writes the visible viewport's
    height to `--app-height`, holds document scroll at zero), narrowed to a
    coarse pointer under the console's mobile breakpoint. Desktop staff usage
    — the vast majority of this app — never attaches a single listener.

    This alone does not stop the header from being dragged: that pan comes
    from WebKit's own focus handling and has to be suppressed at the point of
    focus, which is what @ferrivia/chat-ui's Composer does. This hook is what
    holds the result still afterwards — see useAppViewport's comment for why
    both halves are needed. */
export function useMobileKeyboardScrollLock(): void {
  useEffect(() => {
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const root = document.documentElement;
    const viewport = window.visualViewport;
    let frame: number | null = null;
    let pumpUntil = 0;
    let applied = 0;
    let pending = 0;
    let stabilityTimer: ReturnType<typeof setTimeout> | null = null;
    let active = false;

    function measure(): number {
      return viewport ? viewport.height * viewport.scale : window.innerHeight;
    }

    function lockScroll() {
      const scroller = document.scrollingElement;
      if (scroller && scroller.scrollTop !== 0) {
        scroller.scrollTop = 0;
      }
      if (window.scrollX !== 0 || window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    }

    function clearStability() {
      if (stabilityTimer !== null) {
        clearTimeout(stabilityTimer);
        stabilityTimer = null;
      }
    }

    function commit(height: number) {
      applied = height;
      pending = height;
      root.style.setProperty("--app-height", `${height}px`);
    }

    function write() {
      const measured = measure();

      if (measured >= applied) {
        clearStability();
        commit(measured);
        return;
      }

      if (measured === pending) {
        return;
      }
      pending = measured;
      clearStability();
      stabilityTimer = setTimeout(() => {
        stabilityTimer = null;
        commit(pending);
      }, STABILITY_MS);
    }

    function tick() {
      frame = null;
      lockScroll();
      write();
      if (performance.now() < pumpUntil) {
        frame = requestAnimationFrame(tick);
      }
    }

    function schedule() {
      lockScroll();
      write();
      pumpUntil = performance.now() + KEYBOARD_SETTLE_MS;
      frame ??= requestAnimationFrame(tick);
    }

    function attach() {
      lockScroll();
      commit(measure());
      document.addEventListener("focusin", schedule);
      document.addEventListener("focusout", schedule);
      window.addEventListener("scroll", schedule, { passive: true });
      viewport?.addEventListener("resize", schedule);
      viewport?.addEventListener("scroll", schedule);
      window.addEventListener("resize", schedule);
      window.addEventListener("orientationchange", schedule);
    }

    function detach() {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
      clearStability();
      document.removeEventListener("focusin", schedule);
      document.removeEventListener("focusout", schedule);
      window.removeEventListener("scroll", schedule);
      viewport?.removeEventListener("resize", schedule);
      viewport?.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      root.style.removeProperty("--app-height");
    }

    function sync() {
      if (mobileQuery.matches && !active) {
        active = true;
        attach();
      } else if (!mobileQuery.matches && active) {
        active = false;
        detach();
      }
    }

    sync();
    mobileQuery.addEventListener("change", sync);

    return () => {
      mobileQuery.removeEventListener("change", sync);
      if (active) {
        detach();
      }
    };
  }, []);
}
